import { Typography } from "@mui/material";
import { PartyIdentificationImagesViewer } from "Components/Shared/PartyIdentificationImages/PartyIdentificationImagesViewer";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ValueItems } from "Components/Shared/ValuesBox/ValueItems";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { useCheckPermission } from "Hooks/usePermission";
import { type PartyDto, Permission } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const IDENTIFICATION_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification; //TODO 561: share translations better

type Props = {
  partyData: PartyDto;
};

export const DocumentsSection = ({ partyData }: Props) => {
  const { t } = useResource();
  const canReadIDDocuments = useCheckPermission(
    AccessRightCodes.IDDocuments,
    Permission.Read,
  );

  if (!canReadIDDocuments) {
    return null;
  }

  const {
    partyIdentification,
    hasIdentificationsUploaded,
    lastName,
    publicID,
    companyName,
  } = partyData;

  const fileNamePrefix =
    `${t(IDENTIFICATION_RESOURCES.IdentificationsDownloadPrefix)} ${companyName || ""} ${lastName || ""}`.replaceAll(
      " ",
      "_",
    );

  if (!publicID) {
    return <ErrorText>{t(Resources.Error.DocumentsFailedToLoad)}</ErrorText>;
  }

  return (
    <ValuesBox title={t(Resources.AML.Documents)}>
      <ValueItems
        values={[
          {
            label: t(IDENTIFICATION_RESOURCES.Identification),
            value: partyIdentification?.identification
              ? t(
                  Resources.CodeLists.IdentificationList[
                    partyIdentification.identification
                  ],
                )
              : t(Resources.Common.Unknown),
          },
          {
            label: t(IDENTIFICATION_RESOURCES.IdentificationNumber),
            value:
              partyIdentification?.identificationNumber ||
              t(Resources.Common.Unknown),
          },
          {
            label: t(IDENTIFICATION_RESOURCES.IdentificationIssuer),
            value:
              partyIdentification?.identificationIssuer ||
              t(Resources.Common.Unknown),
          },
          {
            label: t(IDENTIFICATION_RESOURCES.IdentificationValidFrom),
            value: partyIdentification?.identificationValidFrom
              ? formatDate(partyIdentification.identificationValidFrom)
              : t(Resources.Common.Unknown),
          },
          {
            label: t(IDENTIFICATION_RESOURCES.IdentificationValidTo),
            value: partyIdentification?.identificationValidTo
              ? formatDate(partyIdentification.identificationValidTo)
              : t(Resources.Common.Unknown),
          },
        ]}
      />
      {hasIdentificationsUploaded ? (
        <PartyIdentificationImagesViewer
          partyPublicID={publicID || ""}
          fileNamePrefix={fileNamePrefix}
        />
      ) : (
        <Typography>{t(Resources.AML.NoDocuments)}</Typography>
      )}
    </ValuesBox>
  );
};
